import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, Square } from "lucide-react";

import { pickFrenchVoice, devicePitch } from "@/lib/deviceVoice";

export default function SpeakButton({ text, parts, onPart, controllerRef, label = "Écouter la lecture", size = "lg", testId = "speak-button" }) {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const voiceRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) { setSupported(false); return; }
    const loadVoices = () => { voiceRef.current = pickFrenchVoice(); };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => { try { window.speechSynthesis.cancel(); } catch { /* noop */ } };
  }, []);

  const stop = useCallback(() => {
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setSpeaking(false);
    onPart?.(null);
  }, [onPart]);

  const speakFrom = useCallback((start = 0) => {
    if (!window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    if (!voiceRef.current) voiceRef.current = pickFrenchVoice();
    const chunks = parts?.length ? parts : [text];
    const done = () => { setSpeaking(false); onPart?.(null); };
    chunks.forEach((chunk, i) => {
      if (i < start) return;
      const u = new SpeechSynthesisUtterance(chunk);
      u.lang = "fr-FR";
      if (voiceRef.current) u.voice = voiceRef.current;
      u.rate = 0.9;   // posé
      u.pitch = devicePitch();
      u.volume = 1;
      u.onstart = () => onPart?.(i);
      if (i === chunks.length - 1) u.onend = done;
      u.onerror = (e) => { if (e.error !== "interrupted" && e.error !== "canceled") done(); };
      window.speechSynthesis.speak(u);
    });
    setSpeaking(true);
  }, [text, parts, onPart]);
  const speak = useCallback(() => speakFrom(0), [speakFrom]);

  useEffect(() => {
    if (!controllerRef) return;
    controllerRef.current = { goTo: (i) => speakFrom(i + 1) };
    return () => { controllerRef.current = null; };
  }, [controllerRef, speakFrom]);

  const toggle = () => (speaking ? stop() : speak());

  if (!supported) return null;

  const big = size === "lg";
  return (
    <button onClick={toggle} data-testid={testId}
      className={`inline-flex items-center justify-center gap-3 rounded-full font-patient font-black text-white active:scale-[0.98] transition-transform ${big ? "h-16 px-8 text-2xl" : "h-12 px-6 text-lg"} ${speaking ? "bg-rose-500" : "bg-emerald-700"}`}
      style={speaking ? {} : { background: "#2E8EC2" }}>
      {speaking ? <Square className={big ? "w-8 h-8" : "w-6 h-6"} /> : <Volume2 className={big ? "w-8 h-8" : "w-6 h-6"} />}
      {speaking ? "Arrêter la lecture" : label}
    </button>
  );
}
